// lib/koi-pond-v3/shaders/depthFog.ts
//
// Shared volumetric depth fog snippet. Inserted into every v3 scene
// material's fragment shader so far surfaces fade toward the deep-water
// tone uniformly. Single source of truth — future scene additions
// (koi material, water surface, lotus pads, stones) compose by adding
// the same uniforms + snippet without re-deriving the math.
//
// Math: exponential distance fog. Cheaper than per-fragment depth
// sampling, smooth falloff, calibrated by uFogDensity.
//
// fogDist = length(cameraPosition - vWorldPos)
// fogFactor = 1 - exp(-uFogDensity * fogDist)
// color = mix(color, uFogColor, fogFactor)
//
// At default uFogDensity=0.08:
//   - 5 world units away → fogFactor ≈ 0.33 (subtle haze)
//   - 10 world units away → fogFactor ≈ 0.55 (clearly hazed)
//   - 20 world units away → fogFactor ≈ 0.80 (mostly fog)
//
// The 20x14 floor extends 7 units to each edge in z, so far edges
// land in the "clearly hazed" range — exactly the depth-receding
// feel the brief asks for.

import { Color } from 'three';
import { palette } from '../palette';

// Drop into fragment shader's uniform declarations:
export const DEPTH_FOG_UNIFORM_DECL = `
  uniform vec3 uFogColor;
  uniform float uFogDensity;
`;

// Drop into fragment shader main() right before gl_FragColor write.
// Requires `vWorldPos` varying + a `vec3 color` local already computed.
export const DEPTH_FOG_APPLY_SNIPPET = `
  float fogDist = length(cameraPosition - vWorldPos);
  float fogFactor = 1.0 - exp(-uFogDensity * fogDist);
  color = mix(color, uFogColor, fogFactor);
`;

// Default values. Pass these (by reference, not clone) into every
// material's uniforms so changing uFogDensity in one place updates
// every shader on the next frame.
export function makeSharedFogUniforms(): {
  uFogColor: { value: Color };
  uFogDensity: { value: number };
} {
  return {
    uFogColor: { value: new Color(palette.deepWater.hex) },
    uFogDensity: { value: 0.08 },
  };
}
